import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useEliminarItem } from "@/hooks/useCatalogo";
import { getApiErrorMessage } from "@/lib/api";
import { type ItemDeCatalogo, type RecursoDeCatalogo } from "@/lib/catalogos";

interface EliminarItemDialogProps {
  recurso: RecursoDeCatalogo;
  /** Ítem a eliminar; `null` cierra el diálogo. */
  item: ItemDeCatalogo | null;
  /** Rótulo en singular del catálogo ("contexto", "ítem de revisión"). */
  singular: string;
  onCerrar: () => void;
}

/**
 * Confirmación de borrado de un ítem de catálogo.
 *
 * El borrado es LÓGICO: la fila sobrevive con su `orden`, y volver a dar de
 * alta el mismo nombre la restaura en su posición original. Por eso el aviso
 * no promete que "se pierde", que sería falso.
 *
 * A diferencia de `EliminarContenidoDialog`, acá NO hay rama de rechazo por
 * referencia: estos catálogos todavía no tienen ninguna tabla que los apunte
 * (son de fases posteriores), así que el backend nunca responde 422. Un error
 * es un error de verdad —red, 500— y se muestra como tal, con reintento.
 */
export function EliminarItemDialog({
  recurso,
  item,
  singular,
  onCerrar,
}: EliminarItemDialogProps) {
  const [error, setError] = useState<string | null>(null);

  /* Con el diálogo desmontado la alerta de adentro no la ve nadie: ahí el
     toast es la única señal posible (ver `useEliminarItem`). */
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const eliminar = useEliminarItem(recurso, {
    onSuccess: () => onCerrar(),
    onError: (fallo) => {
      const mensaje = getApiErrorMessage(
        fallo,
        `No pudimos eliminar el ${singular}. Inténtalo de nuevo.`,
      );

      if (montado.current) {
        setError(mensaje);
        return;
      }

      toast.error(mensaje);
    },
  });

  if (!item) return null;

  // `isPending` NO alcanza para bloquear el cierre: sin red, TanStack deja la
  // mutación PAUSADA con `isPending` en true sin haber enviado nada. Como el
  // diálogo es modal y atrapa el foco, condicionar las tres salidas —clic
  // afuera, Escape y Cancelar— solo a `isPending` dejaría al usuario
  // encerrado en la app hasta recuperar conexión o recargar. Pausada NO es en
  // vuelo: si el pedido nunca salió, cerrar es seguro.
  const enVuelo = eliminar.isPending && !eliminar.isPaused;

  return (
    <AlertDialog
      open
      onOpenChange={(abierto) => {
        if (!abierto && !enVuelo) onCerrar();
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            ¿Eliminar «{item.nombre}» del catálogo?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Deja de ofrecerse en el sistema. Si más adelante vuelves a crear un {singular}{" "}
            con el mismo nombre, se restaura en esta misma posición.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div
            role="alert"
            className="bg-danger-soft text-danger-soft-foreground rounded-md px-3.5 py-3 text-sm"
          >
            <p className="font-bold">{error}</p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={enVuelo}>Cancelar</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              eliminar.mutate(item.id);
            }}
            disabled={enVuelo}
            className="gap-2"
          >
            {enVuelo && <Spinner />}
            Eliminar {singular}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
